import { type FC, useMemo, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { useRolePermissions } from '@/hooks/useRolePermissions';
import { Drawer, EmptyState, FilterBar, LoadingState, SearchInput, useToast } from '@/components/base';
import { type CreateLegalChecklistInput, type LegalChecklist, useLegalChecklists } from '@/hooks/useLegalChecklists';

type ChecklistItemDraft = {
  localId: string;
  title: string;
  description: string;
  sortOrder: string;
  isRequired: boolean;
  isActive: boolean;
};

type ChecklistFormState = {
  name: string;
  legalArea: string;
  serviceType: string;
  description: string;
  complexity: 'low' | 'medium' | 'high';
  practicalNotes: string;
  tags: string;
  isPinned: boolean;
  items: ChecklistItemDraft[];
};

const complexityLabels: Record<ChecklistFormState['complexity'], string> = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
};

const emptyItemDraft = (): ChecklistItemDraft => ({
  localId:
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
  title: '',
  description: '',
  sortOrder: '',
  isRequired: false,
  isActive: true,
});

const emptyFormState = (): ChecklistFormState => ({
  name: '',
  legalArea: '',
  serviceType: '',
  description: '',
  complexity: 'medium',
  practicalNotes: '',
  tags: '',
  isPinned: false,
  items: [emptyItemDraft()],
});

const checklistToFormState = (checklist: LegalChecklist): ChecklistFormState => ({
  name: checklist.name,
  legalArea: checklist.legalArea,
  serviceType: checklist.serviceType ?? '',
  description: checklist.description ?? '',
  complexity: checklist.complexity,
  practicalNotes: checklist.practicalNotes ?? '',
  tags: checklist.tags,
  isPinned: checklist.isPinned,
  items:
    checklist.items.length > 0
      ? checklist.items.map((item, index) => ({
          localId: item.id || `${checklist.id}-${index}`,
          title: item.title,
          description: item.description ?? '',
          sortOrder: String(item.sortOrder || index + 1),
          isRequired: item.isRequired,
          isActive: item.isActive,
        }))
      : [emptyItemDraft()],
});

const formatTags = (tags: string) =>
  tags
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);

const formatItemCount = (checklist: LegalChecklist) => checklist.itemCount || checklist.items.length;

const buildPayload = (form: ChecklistFormState): CreateLegalChecklistInput => ({
  name: form.name.trim(),
  legalArea: form.legalArea.trim(),
  serviceType: form.serviceType.trim() || null,
  description: form.description.trim() || null,
  complexity: form.complexity,
  practicalNotes: form.practicalNotes.trim() || null,
  tags: form.tags.trim() || null,
  isPinned: form.isPinned,
  isActive: true,
  items: form.items
    .map((item, index) => ({
      title: item.title.trim(),
      description: item.description.trim() || null,
      sortOrder: Number.isFinite(Number(item.sortOrder)) ? Number(item.sortOrder) : index + 1,
      isRequired: item.isRequired,
      isActive: item.isActive,
    }))
    .filter((item) => item.title !== ''),
});

const ChecklistsPage: FC = () => {
  const { canManageLegalStructures } = useRolePermissions();
  const { addToast } = useToast();
  const {
    legalChecklists,
    isLoadingLegalChecklists,
    isSavingLegalChecklists,
    legalChecklistsError,
    legalChecklistsSuccess,
    loadLegalChecklists,
    createLegalChecklist,
    updateLegalChecklist,
    archiveLegalChecklist,
    togglePinLegalChecklist,
    clearLegalChecklistsFeedback,
  } = useLegalChecklists();
  const [search, setSearch] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [complexityFilter, setComplexityFilter] = useState('');
  const [showDrawer, setShowDrawer] = useState(false);
  const [editingChecklistId, setEditingChecklistId] = useState<string | null>(null);
  const [expandedChecklistId, setExpandedChecklistId] = useState<string | null>(null);
  const [form, setForm] = useState<ChecklistFormState>(emptyFormState());
  const [localMessage, setLocalMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const areaOptions = useMemo(
    () => Array.from(new Set(legalChecklists.map((checklist) => checklist.legalArea).filter(Boolean))).sort((a, b) =>
      a.localeCompare(b, 'pt-BR')
    ),
    [legalChecklists]
  );

  const filteredChecklists = useMemo(() => {
    const query = search.trim().toLowerCase();

    return legalChecklists.filter((checklist) => {
      if (areaFilter && checklist.legalArea !== areaFilter) {
        return false;
      }

      if (complexityFilter && checklist.complexity !== complexityFilter) {
        return false;
      }

      if (!query) {
        return true;
      }

      const haystack = [
        checklist.name,
        checklist.legalArea,
        checklist.serviceType,
        checklist.description,
        checklist.practicalNotes,
        checklist.tags,
        ...checklist.items.map((item) => `${item.title} ${item.description ?? ''}`),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [areaFilter, complexityFilter, legalChecklists, search]);

  const openCreateDrawer = () => {
    setEditingChecklistId(null);
    setForm(emptyFormState());
    setShowDrawer(true);
    clearLegalChecklistsFeedback();
    setLocalMessage(null);
  };

  const openEditDrawer = (checklist: LegalChecklist) => {
    setEditingChecklistId(checklist.id);
    setForm(checklistToFormState(checklist));
    setShowDrawer(true);
    clearLegalChecklistsFeedback();
    setLocalMessage(null);
  };

  const closeDrawer = () => {
    if (isSavingLegalChecklists) {
      return;
    }

    setShowDrawer(false);
    setEditingChecklistId(null);
    setForm(emptyFormState());
  };

  const clearFilters = () => {
    setSearch('');
    setAreaFilter('');
    setComplexityFilter('');
  };

  const updateItem = (localId: string, field: keyof ChecklistItemDraft, value: string | boolean) => {
    setForm((current) => ({
      ...current,
      items: current.items.map((item) => (item.localId === localId ? { ...item, [field]: value } : item)),
    }));
  };

  const addItem = () => {
    setForm((current) => ({
      ...current,
      items: [...current.items, emptyItemDraft()],
    }));
  };

  const removeItem = (localId: string) => {
    setForm((current) => {
      const nextItems = current.items.filter((item) => item.localId !== localId);
      return {
        ...current,
        items: nextItems.length > 0 ? nextItems : [emptyItemDraft()],
      };
    });
  };

  const handleSave = async () => {
    const payload = buildPayload(form);

    if (!payload.name) {
      setLocalMessage({ kind: 'error', text: 'O nome do checklist é obrigatório.' });
      return;
    }

    if (!payload.legalArea) {
      setLocalMessage({ kind: 'error', text: 'A área jurídica é obrigatória.' });
      return;
    }

    if (!payload.complexity) {
      setLocalMessage({ kind: 'error', text: 'A complexidade é obrigatória.' });
      return;
    }

    try {
      if (editingChecklistId) {
        await updateLegalChecklist(editingChecklistId, payload);
        addToast('Checklist atualizado com sucesso.', 'success');
      } else {
        await createLegalChecklist(payload);
        addToast('Checklist criado com sucesso.', 'success');
      }

      setShowDrawer(false);
      setEditingChecklistId(null);
      setForm(emptyFormState());
      setLocalMessage(null);
      await loadLegalChecklists();
    } catch (error: any) {
      const message = error?.message || 'Não foi possível salvar o checklist.';
      setLocalMessage({ kind: 'error', text: message });
      addToast(message, 'error');
    }
  };

  const handleArchive = async (checklist: LegalChecklist) => {
    if (!window.confirm('Tem certeza que deseja arquivar este checklist? Esta ação removerá o checklist da listagem, mas manterá o registro no banco para auditoria.')) {
      return;
    }

    try {
      await archiveLegalChecklist(checklist.id);
      if (expandedChecklistId === checklist.id) {
        setExpandedChecklistId(null);
      }
      if (editingChecklistId === checklist.id) {
        setShowDrawer(false);
        setEditingChecklistId(null);
        setForm(emptyFormState());
      }
      addToast('Checklist arquivado com sucesso.', 'success');
      await loadLegalChecklists();
    } catch (error: any) {
      const message = error?.message || 'Não foi possível arquivar o checklist.';
      setLocalMessage({ kind: 'error', text: message });
      addToast(message, 'error');
    }
  };

  const handleTogglePin = async (checklist: LegalChecklist) => {
    try {
      await togglePinLegalChecklist(checklist.id);
      addToast(checklist.isPinned ? 'Checklist desfixado com sucesso.' : 'Checklist fixado com sucesso.', 'success');
      await loadLegalChecklists();
    } catch (error: any) {
      const message = error?.message || 'Não foi possível alterar a fixação do checklist.';
      setLocalMessage({ kind: 'error', text: message });
      addToast(message, 'error');
    }
  };

  const showEmptyState = !isLoadingLegalChecklists && filteredChecklists.length === 0;

  return (
    <AppLayout>
      <div className="p-4 md:p-6 space-y-5">

        {!canManageLegalStructures ? (
          <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800">
            Modo consulta: seu perfil pode visualizar este módulo, mas alterações estruturais são reservadas a administradores e advogados.
          </div>
        ) : null}
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="space-y-1">
            <h2 className="text-xl font-bold text-foreground-900">Checklists Operacionais</h2>
            <p className="text-sm text-foreground-500">
              Central interna para cadastrar, organizar e personalizar os checklists utilizados no escritório.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => void loadLegalChecklists()}
              className="inline-flex items-center gap-2 rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
            >
              <i className="ri-refresh-line" />
              Atualizar
            </button>
            <button
            hidden={!canManageLegalStructures}
              type="button"
              onClick={openCreateDrawer}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-500 px-3 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-add-line" />
              Novo checklist
            </button>
          </div>
        </div>

        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Checklists internos de apoio operacional. Os itens devem ser adaptados conforme estratégia, documentos
          disponíveis, fase processual, exigências do juízo/órgão e peculiaridades do caso concreto.
        </div>

        {legalChecklistsError || legalChecklistsSuccess || localMessage ? (
          <div
            className={`rounded-xl px-4 py-3 text-sm ${
              localMessage?.kind === 'error' || legalChecklistsError
                ? 'border border-amber-200 bg-amber-50 text-amber-800'
                : 'border border-emerald-200 bg-emerald-50 text-emerald-800'
            }`}
          >
            {localMessage?.text || legalChecklistsError || legalChecklistsSuccess}
          </div>
        ) : null}

        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Buscar por nome, área, tipo, observações, itens ou tags..."
            className="w-full lg:max-w-xl"
          />

          <button
            type="button"
            onClick={clearFilters}
            className="rounded-lg border border-background-200 px-3 py-2 text-xs font-medium text-foreground-600 transition-colors hover:bg-background-100"
          >
            Limpar filtros
          </button>
        </div>

        <FilterBar
          filters={[
            {
              key: 'area',
              label: 'Área',
              value: areaFilter,
              onChange: setAreaFilter,
              options: areaOptions.map((area) => ({ label: area, value: area })),
            },
            {
              key: 'complexity',
              label: 'Complexidade',
              value: complexityFilter,
              onChange: setComplexityFilter,
              options: Object.entries(complexityLabels).map(([value, label]) => ({ label, value })),
            },
          ]}
        />

        {isLoadingLegalChecklists ? (
          <LoadingState message="Carregando checklists jurídicos..." />
        ) : showEmptyState ? (
          <EmptyState
            icon="ri-checkbox-multiple-line"
            title="Nenhum checklist cadastrado"
            description="Cadastre seus próprios checklists jurídicos para manter esta central personalizada e útil ao fluxo do escritório."
            action={canManageLegalStructures ? { label: 'Cadastrar checklist', onClick: openCreateDrawer, icon: 'ri-add-line' } : undefined}
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {filteredChecklists.map((checklist) => {
              const isExpanded = expandedChecklistId === checklist.id;
              const isHighComplexity = checklist.complexity === 'high';

              return (
                <article
                  key={checklist.id}
                  className={`rounded-2xl border p-4 shadow-sm ${
                    isHighComplexity ? 'border-red-200 bg-red-50' : 'border-background-200 bg-background-50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => setExpandedChecklistId((current) => (current === checklist.id ? null : checklist.id))}
                      className="flex-1 text-left"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-sm font-semibold text-foreground-900">{checklist.name}</h3>
                        {checklist.isPinned ? (
                          <span className="rounded-full border border-primary-200 bg-primary-50 px-2 py-0.5 text-[10px] font-medium text-primary-700">
                            Fixado
                          </span>
                        ) : null}
                      </div>
                      <p className="mt-0.5 text-xs text-foreground-500">
                        {checklist.legalArea} · {checklist.serviceType || 'Serviço não informado'}
                      </p>
                    </button>

                    <div className="flex items-center gap-2">
                      <button
            hidden={!canManageLegalStructures}
                        type="button"
                        onClick={() => void handleTogglePin(checklist)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-background-200 text-foreground-600 transition-colors hover:bg-background-100"
                        title={checklist.isPinned ? 'Desfixar' : 'Fixar'}
                      >
                        <i className={checklist.isPinned ? 'ri-star-fill text-primary-600' : 'ri-star-line'} />
                      </button>
                      <button
            hidden={!canManageLegalStructures}
                        type="button"
                        onClick={() => openEditDrawer(checklist)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-background-200 text-foreground-600 transition-colors hover:bg-background-100"
                        title="Editar"
                      >
                        <i className="ri-edit-line" />
                      </button>
                      <button
            hidden={!canManageLegalStructures}
                        type="button"
                        onClick={() => void handleArchive(checklist)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-background-200 text-foreground-600 transition-colors hover:bg-background-100"
                        title="Arquivar"
                      >
                        <i className="ri-archive-line" />
                      </button>
                    </div>
                  </div>

                  <p className="mt-3 text-sm text-foreground-700">{checklist.description || 'Sem descrição informada.'}</p>

                  <div className="mt-4 grid gap-3 text-sm md:grid-cols-2">
                    <div>
                      <p className="text-[11px] text-foreground-500">Complexidade</p>
                      <p
                        className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium ${
                          checklist.complexity === 'high'
                            ? 'border-red-200 bg-red-100 text-red-700'
                            : checklist.complexity === 'medium'
                            ? 'border-amber-200 bg-amber-100 text-amber-700'
                            : 'border-emerald-200 bg-emerald-100 text-emerald-700'
                        }`}
                      >
                        {complexityLabels[checklist.complexity]}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] text-foreground-500">Itens</p>
                      <p className="font-medium text-foreground-800">{formatItemCount(checklist)}</p>
                    </div>
                    <div>
                      <p className="text-[11px] text-foreground-500">Observações</p>
                      <p className="font-medium text-foreground-800">{checklist.practicalNotes || 'Sem observações'}</p>
                    </div>
                    <div>
                      <p className="text-[11px] text-foreground-500">Área</p>
                      <p className="font-medium text-foreground-800">{checklist.legalArea}</p>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {formatTags(checklist.tags).length > 0 ? (
                      formatTags(checklist.tags).map((tag) => (
                        <span
                          key={`${checklist.id}-${tag}`}
                          className="rounded-full border border-background-200 bg-background-100 px-2 py-0.5 text-[10px] text-foreground-600"
                        >
                          #{tag}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-foreground-500">Sem tags</span>
                    )}
                  </div>

                  {isExpanded ? (
                    <div className="mt-4 space-y-3 border-t border-background-200 pt-3">
                      <div>
                        <p className="text-[11px] text-foreground-500">Itens do checklist</p>
                        {checklist.items.length > 0 ? (
                          <ul className="mt-2 space-y-2">
                            {checklist.items.map((item) => (
                              <li
                                key={item.id}
                                className="rounded-lg border border-background-200 bg-background-100 px-3 py-2 text-sm text-foreground-700"
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div>
                                    <p className="font-medium text-foreground-800">{item.title}</p>
                                    {item.description ? (
                                      <p className="mt-0.5 text-xs text-foreground-500">{item.description}</p>
                                    ) : null}
                                  </div>
                                  <div className="flex flex-col items-end gap-1">
                                    <span className="text-[10px] text-foreground-500">Ordem {item.sortOrder}</span>
                                    {item.isRequired ? (
                                      <span className="rounded-full border border-primary-200 bg-primary-50 px-2 py-0.5 text-[10px] font-medium text-primary-700">
                                        Obrigatório
                                      </span>
                                    ) : null}
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="mt-2 text-sm text-foreground-500">Nenhum item ativo cadastrado.</p>
                        )}
                      </div>
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        )}

        {!isLoadingLegalChecklists && filteredChecklists.length === 0 && legalChecklists.length > 0 ? (
          <div className="rounded-xl border border-background-200 bg-background-50 p-5 text-sm text-foreground-600">
            Nenhum checklist encontrado com os filtros atuais.
          </div>
        ) : null}
      </div>

      <Drawer
        isOpen={canManageLegalStructures && showDrawer}
        onClose={closeDrawer}
        title={editingChecklistId ? 'Editar checklist' : 'Novo checklist'}
        width="w-[920px]"
        footer={
          <>
            <button
              type="button"
              onClick={closeDrawer}
              className="rounded-lg border border-background-200 px-3 py-2 text-sm font-medium text-foreground-600 transition-colors hover:bg-background-100"
            >
              Cancelar
            </button>
            <button
            hidden={!canManageLegalStructures}
              type="button"
              onClick={() => void handleSave()}
              disabled={isSavingLegalChecklists}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-500 px-3 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSavingLegalChecklists ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-line" />}
              {editingChecklistId ? 'Salvar alterações' : 'Criar checklist'}
            </button>
          </>
        }
      >
        <div className="space-y-5">
          <div className="grid gap-3 md:grid-cols-2">
            <label className="space-y-1">
              <span className="block text-xs font-medium text-foreground-600">Nome *</span>
              <input
                type="text"
                value={form.name}
                onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                placeholder="Ex.: Ação de saúde — medicamento/cirurgia"
                className="w-full rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-300 focus:outline-none"
              />
            </label>

            <label className="space-y-1">
              <span className="block text-xs font-medium text-foreground-600">Área jurídica *</span>
              <input
                type="text"
                value={form.legalArea}
                onChange={(event) => setForm((current) => ({ ...current, legalArea: event.target.value }))}
                placeholder="Ex.: Saúde"
                className="w-full rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-300 focus:outline-none"
              />
            </label>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <label className="space-y-1">
              <span className="block text-xs font-medium text-foreground-600">Tipo de serviço</span>
              <input
                type="text"
                value={form.serviceType}
                onChange={(event) => setForm((current) => ({ ...current, serviceType: event.target.value }))}
                placeholder="Ex.: Ação judicial, defesa administrativa"
                className="w-full rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-300 focus:outline-none"
              />
            </label>

            <label className="space-y-1">
              <span className="block text-xs font-medium text-foreground-600">Complexidade *</span>
              <select
                value={form.complexity}
                onChange={(event) => setForm((current) => ({ ...current, complexity: event.target.value as ChecklistFormState['complexity'] }))}
                className="w-full rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-300 focus:outline-none"
              >
                {Object.entries(complexityLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <label className="space-y-1">
              <span className="block text-xs font-medium text-foreground-600">Descrição curta</span>
              <textarea
                value={form.description}
                onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                rows={2}
                placeholder="Resumo curto do checklist."
                className="w-full rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-300 focus:outline-none"
              />
            </label>

            <label className="space-y-1">
              <span className="block text-xs font-medium text-foreground-600">Observações práticas</span>
              <textarea
                value={form.practicalNotes}
                onChange={(event) => setForm((current) => ({ ...current, practicalNotes: event.target.value }))}
                rows={2}
                placeholder="Observações úteis para a operação."
                className="w-full rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-300 focus:outline-none"
              />
            </label>
          </div>

          <div className="grid gap-3 md:grid-cols-[1fr_180px]">
            <label className="space-y-1">
              <span className="block text-xs font-medium text-foreground-600">Tags</span>
              <input
                type="text"
                value={form.tags}
                onChange={(event) => setForm((current) => ({ ...current, tags: event.target.value }))}
                placeholder="Ex.: saúde, liminar, urgência"
                className="w-full rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-300 focus:outline-none"
              />
            </label>

            <label className="space-y-1">
              <span className="block text-xs font-medium text-foreground-600">Fixado</span>
              <button
                type="button"
                onClick={() => setForm((current) => ({ ...current, isPinned: !current.isPinned }))}
                className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                  form.isPinned
                    ? 'border-primary-200 bg-primary-50 text-primary-700'
                    : 'border-background-200 bg-background-50 text-foreground-700'
                }`}
              >
                <span>{form.isPinned ? 'Sim' : 'Não'}</span>
                <i className={form.isPinned ? 'ri-star-fill' : 'ri-star-line'} />
              </button>
            </label>
          </div>

          <div className="rounded-2xl border border-background-200 bg-background-50">
            <div className="flex items-center justify-between gap-3 border-b border-background-100 px-4 py-3">
              <div>
                <h4 className="text-sm font-semibold text-foreground-800">Itens do checklist</h4>
                <p className="text-xs text-foreground-500">Adicione os itens necessários e marque os obrigatórios.</p>
              </div>
              <button
            hidden={!canManageLegalStructures}
                type="button"
                onClick={addItem}
                className="inline-flex items-center gap-2 rounded-lg border border-background-200 px-3 py-2 text-sm font-medium text-foreground-600 transition-colors hover:bg-background-100"
              >
                <i className="ri-add-line" />
                Adicionar item
              </button>
            </div>

            <div className="space-y-3 p-4">
              {form.items.map((item, index) => (
                <div key={item.localId} className="rounded-xl border border-background-200 bg-background-50 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500">
                        Item {index + 1}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => updateItem(item.localId, 'isRequired', !item.isRequired)}
                        className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${
                          item.isRequired
                            ? 'border-primary-200 bg-primary-50 text-primary-700'
                            : 'border-background-200 bg-background-100 text-foreground-600'
                        }`}
                      >
                        {item.isRequired ? 'Obrigatório' : 'Opcional'}
                      </button>
                      <button
                        type="button"
                        onClick={() => updateItem(item.localId, 'isActive', !item.isActive)}
                        className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${
                          item.isActive
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                            : 'border-background-200 bg-background-100 text-foreground-600'
                        }`}
                      >
                        {item.isActive ? 'Ativo' : 'Arquivado'}
                      </button>
                      <button
            hidden={!canManageLegalStructures}
                        type="button"
                        onClick={() => removeItem(item.localId)}
                        className="rounded-full border border-background-200 px-2 py-0.5 text-[10px] font-medium text-foreground-600 hover:bg-background-100"
                      >
                        Remover
                      </button>
                    </div>
                  </div>

                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                    <label className="space-y-1">
                      <span className="block text-xs font-medium text-foreground-600">Título</span>
                      <input
                        type="text"
                        value={item.title}
                        onChange={(event) => updateItem(item.localId, 'title', event.target.value)}
                        placeholder="Ex.: documento pessoal do cliente"
                        className="w-full rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-300 focus:outline-none"
                      />
                    </label>

                    <label className="space-y-1">
                      <span className="block text-xs font-medium text-foreground-600">Ordem</span>
                      <input
                        type="number"
                        min="1"
                        value={item.sortOrder}
                        onChange={(event) => updateItem(item.localId, 'sortOrder', event.target.value)}
                        className="w-full rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-300 focus:outline-none"
                      />
                    </label>
                  </div>

                  <div className="mt-3 grid gap-3">
                    <label className="space-y-1">
                      <span className="block text-xs font-medium text-foreground-600">Descrição</span>
                      <textarea
                        value={item.description}
                        onChange={(event) => updateItem(item.localId, 'description', event.target.value)}
                        rows={2}
                        placeholder="Observação ou detalhe do item."
                        className="w-full rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-300 focus:outline-none"
                      />
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Drawer>
    </AppLayout>
  );
};

export default ChecklistsPage;
